import { type ReactNode, useEffect } from 'react';

import { FormattedMessage } from 'react-intl';
import Layout from '../Layout';
import PlansPanel from './PlansPanel';
import PlansBenefits from './PlansBenefits';
import PlansSceenFooter from './PlansScreenFooter';

import styles from './PlansScreen.module.scss';

import AnalyticsEventHandler from '@/services/analytics/AnalyticsEventHandler';
import { env } from '@/utils/env';
import { type Plan } from '@/utils/mapProductObject';

interface PlansScreenProps {
  isLoading: boolean;
  plans: Plan[];
  onPlanClick: (plan: Plan) => () => void;
  discountLabel?: ReactNode;
}

const PlansScreen = ({ isLoading, plans, onPlanClick, discountLabel }: PlansScreenProps) => {
  const hasTrialLabel = isLoading ? false : Boolean(plans[0]?.hasTrial);
  const trialPeriodLengthDays = isLoading ? 0 : (plans[0]?.trialDays ?? 0);

  useEffect(() => {
    AnalyticsEventHandler.trackScreenPlanShown();
  }, []);

  return (
    <div className="darkTheme">
      <Layout>
        <main className={styles.container}>
          <div className={styles.titleWrapper}>
            <h1 className={styles.title}>
              {hasTrialLabel ? (
                <FormattedMessage
                  id="plansScreen.titleWithTrial"
                  defaultMessage="Start your Premium plan with a <highlight>free {trialPeriodLengthDays, plural, one {24-hour} few {#-day} many {#-day} other {#-day} } trial!</highlight>"
                  description="Title of the Plan selection screen"
                  values={{
                    trialPeriodLengthDays,
                    highlight: (chunk) => <span className={styles.titleHighlight}>{chunk}</span>,
                  }}
                />
              ) : (
                <FormattedMessage
                  id="plansScreen.title"
                  defaultMessage="Start your Premium plan"
                  description="Title of the Plan selection screen"
                />
              )}
            </h1>
            {!isLoading && !discountLabel && (
              <a href={`${env.sitetUrl}/redeem/?from=order`} className={styles.codeRedeemLink}>
                <FormattedMessage
                  id="plansScreen.codeRedeemLink"
                  defaultMessage="Have a promocode?"
                  description="Link on the Plan selection screen leading to code redeem page"
                />
              </a>
            )}
          </div>

          <div className={styles.subtitleLine}>
            {hasTrialLabel && (
              <span className={styles.freeTrialLabel}>
                <FormattedMessage
                  id="plansScreen.trialLabel"
                  defaultMessage="Cancel anytime with a 30-day money-back guarantee."
                  description="Screen-wide trial label of the Plan selection screen"
                />
              </span>
            )}

            {discountLabel && (
              <div className={styles.discountContainer}>
                <picture>
                  <source
                    srcSet="/images/new-order/emoji/money-with-wings-emoji.webm 1x, /images/new-order/emoji/money-with-wings-emoji@2x.webm 2x"
                    type="image/webm"
                  />
                  <img
                    src="/images/new-order/emoji/money-with-wings-emoji.png"
                    srcSet="/images/new-order/emoji/money-with-wings-emoji@2x.png 2x"
                    width="24"
                    height="24"
                    alt=""
                  />
                </picture>

                <span className={styles.discountLabel}>{discountLabel}</span>
              </div>
            )}
          </div>

          <div className={styles.taxExclusionLabel}>
            <FormattedMessage
              id="plansScreen.taxExclusionLabel"
              defaultMessage="*Taxes may apply, depending on your country of residence."
              description="Disclaimer above plan selection panel, warning that displayed prices are tax-excluded."
            />
          </div>

          <PlansPanel plans={plans} isLoading={isLoading} onPlanClick={onPlanClick} />
          <PlansBenefits />
        </main>
        <PlansSceenFooter />
      </Layout>
    </div>
  );
};

export default PlansScreen;
